/**
 * Event header — the poster frame the buying surface puts around
 * `<event-identity>`: `<brand-bar>` above, the artwork beside the identity
 * block, and the whole thing washed in that artwork. The event's own words
 * — when, what, where — are the shared component's, rendered exactly as
 * order-access renders them: the title's size comes from the column it lands
 * in, and this header's column is the widest one it ships to, so the event
 * reads as the page's subject without this view dosing it. Ticket-type
 * selection and quantity are
 * type-selector.view.ts's; this view renders configuration only — an event
 * with no availability data yet renders as open, per event-page.state.ts's
 * own decision on absent availability.
 *
 * `<brand-bar>` renders in all three frames — loading, not-found, ready
 * alike. A buyer who followed a dead link still has the way home and their
 * own language, and the mark never shimmers with the event: it resolves from
 * the workspace card, a source that has nothing to do with which event this
 * route names.
 *
 * The poster leads. Its own art doubles as the header's ground — the same
 * URL, scaled out, heavily blurred and dropped to a low opacity behind the
 * block — so each event colors its own page off artwork the browser has
 * already cached, while the brand mark holds the frame. An event with no
 * poster gets no wash: the container surface stands alone rather than
 * inventing a color the brand never chose. The 2:3 box reserves its aspect
 * BEFORE the image loads (POSTER_WIDTH/HEIGHT as the intrinsic hint), so
 * the name and schedule beside it never jump when artwork arrives late.
 * Tapping the poster opens it in schmancy's `lightbox` — the media viewer,
 * on a near-opaque ground, because a poster at this size is a thumbnail of
 * art the buyer came for and a card surface would frame it rather than
 * show it. The directive owns the whole affordance: it makes this img
 * keyboard-operable (role, tabindex, Enter/Space) and the viewer traps
 * focus, locks the page, and restores focus on close.
 */
import { area } from "@mhmo91/schmancy";
import { lightbox } from "@mhmo91/schmancy/lightbox";
import { SchmancyElement, schmancyElement } from "@mhmo91/schmancy/mixins";
import { Event } from "@momo/types";
import { html, type TemplateResult } from "lit";
import { property, state } from "lit/decorators.js";
import { when } from "lit/directives/when.js";
import { LocalizeMixin } from "../../i18n/localize.ts";
import { organizationPathPrefix } from "../../workspace/organization.state.ts";
import "../brand-bar.view.ts";
import {
	type EventHeaderReady,
	eventHeaderView,
} from "./event-header.state.ts";
import "../event-identity.view.ts";

@schmancyElement("event-header")
export class EventHeaderView extends LocalizeMixin(SchmancyElement) {
	/** Whether the `actions` slot currently has anything in it. Both consumers
	 *  project conditionally and late, so the `<slot>` stays in the shadow DOM
	 *  unconditionally — a slot rendered only once this is true could never
	 *  receive the `slotchange` that would set it — and it is the slot's BOX
	 *  that collapses, because an empty box still spends the identity column's
	 *  24px gap. */
	@state() private hasActions = false;

	/** Raised by the buying page when this browser already holds a fulfilled
	 *  order for the event on screen. The header only forwards it to the
	 *  bar, which answers it over its orders entry. */
	@property({ type: Boolean }) ticketsHere = false;

	private toStorefront() {
		area.push({
			area: "root",
			component: "storefront",
			path: organizationPathPrefix.get() || "/",
			historyStrategy: "push",
		});
	}

	/** The one shell every frame renders into: the brand bar above, the
	 *  frame's own body below, and the poster wash behind both when the
	 *  event has artwork to wash with. */
	private shell(poster: string | null, body: TemplateResult) {
		return html`
			<schmancy-surface
				type="container"
				opacity="translucent"
				class="relative flex flex-col overflow-hidden rounded-b-bezel"
			>
				${when(
					poster,
					() => html`
						<img
							src=${poster ?? ""}
							alt=""
							aria-hidden="true"
							decoding="async"
							class="pointer-events-none absolute inset-0 h-full w-full scale-125 object-cover opacity-25 blur-3xl"
						/>
					`,
				)}

				<brand-bar .ticketsHere=${this.ticketsHere} class="relative px-4 pt-6 sm:px-6"></brand-bar>

				<div class="relative px-4 pb-6 pt-4 sm:px-6">${body}</div>
			</schmancy-surface>
		`;
	}

	/** The resolved frame — poster beside the identity block, both inside the
	 *  shell. `eventHeaderView` resolves the facts from whichever route is
	 *  mounted (the buying page's live event doc, the ticket page's frozen
	 *  order snapshot), so both pages render the identical header from the
	 *  identical call with nothing passed in. The `actions` slot sits in
	 *  normal flow under `<event-identity>`, inside the identity column
	 *  beside the poster — under the venue line, never floating over
	 *  `<brand-bar>` (the logo, the language toggle): order-access projects
	 *  its order-wide chips into it; event-page projects a self-service
	 *  refund entry point. The row exists only while something is assigned:
	 *  the slot itself never leaves the shadow DOM, but its box does. */
	private resolved(event: EventHeaderReady): TemplateResult {
		return this.shell(
			event.poster,
			html`
				<div class="flex items-start gap-4 md:gap-5">
					<div class="aspect-[2/3] w-28 shrink-0 overflow-hidden rounded-xl bg-surface-containerHigh md:w-32">
						${when(
							event.poster,
							() => html`
								<img
									${lightbox({ label: event.name })}
									src=${event.poster ?? ""}
									alt=""
									width=${Event.POSTER_WIDTH}
									height=${Event.POSTER_HEIGHT}
									decoding="async"
									aria-label=${this.t("storefront.eventHeader.posterAriaLabel", { name: event.name })}
									class="h-full w-full object-cover transition-opacity hover:opacity-80"
								/>
							`,
							() => html`
								<div class="flex h-full w-full items-center justify-center">
									<schmancy-icon class="text-4xl text-surface-onVariant/40">confirmation_number</schmancy-icon>
								</div>
							`,
						)}
					</div>

					<div class="flex min-w-0 flex-1 flex-col gap-6">
						<event-identity
							.name=${event.name}
							.description=${event.description ?? null}
							.start=${event.start.toDate()}
							.end=${event.end.toDate()}
							.timezone=${event.timezone}
							.venue=${event.venue}
							.mapsUrl=${event.mapsUrl}
							.notes=${event.notes ?? null}
						></event-identity>

						<div
							class=${this.classMap({
								"flex flex-wrap items-center gap-2": this.hasActions,
								hidden: !this.hasActions,
							})}
						>
							<slot
								name="actions"
								@slotchange=${(e: globalThis.Event) => {
									if (!(e.target instanceof HTMLSlotElement)) return;
									this.hasActions = e.target
										.assignedNodes({ flatten: true })
										.some(
											(node) =>
												node instanceof Element ||
												(node.textContent ?? "").trim() !== "",
										);
								}}
							></slot>
						</div>
					</div>
				</div>
			`,
		);
	}

	/** The waiting frame — only the event's own facts shimmer; the brand bar
	 *  resolves from the workspace card and is already settled. Geometry
	 *  matches the resolved frame's poster + text column, so the arrival of
	 *  the real facts moves nothing. */
	private skeleton(): TemplateResult {
		return this.shell(
			null,
			html`
				<div class="flex items-start gap-4 md:gap-5" aria-hidden="true">
					<schmancy-skeleton class="aspect-[2/3] w-28 shrink-0 rounded-xl md:w-32"></schmancy-skeleton>
					<div class="flex min-w-0 flex-1 flex-col gap-2">
						<schmancy-skeleton class="h-8 w-2/3 rounded-lg"></schmancy-skeleton>
						<schmancy-skeleton class="h-5 w-1/3 rounded-lg"></schmancy-skeleton>
					</div>
				</div>
			`,
		);
	}

	override render() {
		const header = eventHeaderView.get();

		if (header.status === "loading") return this.skeleton();

		if (header.status === "not-found") {
			return this.shell(
				null,
				html`
					<div class="flex flex-col items-center gap-3 py-6 text-center">
						<schmancy-icon class="text-4xl text-surface-onVariant">event_busy</schmancy-icon>
						<schmancy-typography type="headline" token="sm">${this.t("storefront.eventHeader.notFoundTitle")}</schmancy-typography>
						<schmancy-typography type="body" token="md" class="max-w-sm text-surface-onVariant">
							${this.t("storefront.eventHeader.notFoundBody")}
						</schmancy-typography>
						<schmancy-button variant="filled" @click=${() => this.toStorefront()}>
							${this.t("storefront.eventHeader.backToEvents")}
						</schmancy-button>
					</div>
				`,
			);
		}

		return this.resolved(header);
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"event-header": EventHeaderView;
	}
}
